import { useState } from 'react';
import { useScrollAnimation } from '@/hooks/useScrollAnimation';

const faqs = [
  {
    q: 'Who provides custom business signs near me in Austin?',
    a: 'Capital Area Signs provides fully custom commercial signage in Austin TX and across Central Texas. Their team handles everything from design and engineering to fabrication, permitting, and professional installation — making them the go-to sign company for local businesses looking to improve visibility and brand impact.',
  },
  {
    q: 'How much do custom signs cost in Austin?',
    a: 'Custom sign pricing depends on size, materials, lighting, and installation complexity. Most projects are quoted individually to match your brand goals and budget. Capital Area Signs offers free consultations and transparent pricing — call (512) 799-0115 to get a custom quote for your specific project.',
  },
  {
    q: 'How long does it take to get a custom sign installed?',
    a: 'Most signage projects take two to four weeks from concept to installation, depending on design complexity, permitting timelines, and fabrication scope. Larger monument or LED signs may take longer. Capital Area Signs provides clear timelines upfront so your project stays on schedule from day one.',
  },
  {
    q: 'Do you handle sign permits in Austin TX?',
    a: 'Yes — Capital Area Signs manages the entire sign permit process in Austin and surrounding cities. Their team knows local zoning codes, HOA requirements, and city regulations inside and out. This expertise means faster approvals, fewer delays, and a fully compliant installation for your business.',
  },
  {
    q: 'What types of businesses do you serve?',
    a: 'Capital Area Signs serves retail stores, restaurants, corporate offices, industrial facilities, medical practices, property management companies, and more. Any business that needs to improve visibility and reinforce its brand identity is a strong fit for their custom commercial signage services.',
  },
  {
    q: 'Do you install vehicle wraps in Austin Texas?',
    a: 'Yes — Capital Area Signs provides full and partial vehicle wrap services for cars, trucks, vans, and commercial fleets across Austin. Vehicle wraps turn every vehicle into a mobile billboard, generating consistent brand exposure throughout Central Texas at a fraction of traditional advertising costs.',
  },
  {
    q: 'What areas do you serve around Austin?',
    a: 'Capital Area Signs serves Austin and the greater Central Texas area, including Round Rock, Cedar Park, Pflugerville, Georgetown, and Leander. They also cover Kyle, Buda, Hutto, Manor, and surrounding communities. Contact them directly to confirm service availability for your location.',
  },
  {
    q: 'Are illuminated channel letter signs worth it for my business?',
    a: 'Illuminated channel letter signs are one of the highest-ROI signage investments for storefronts. They increase nighttime visibility by up to 40%, reinforce brand identity 24/7, and signal professionalism to customers before they ever walk in. Capital Area Signs custom-engineers every set for maximum impact.',
  },
];

export default function FAQSection() {
  const [open, setOpen] = useState<number | null>(null);
  const { ref, visible } = useScrollAnimation();

  return (
    <section id="faq" className="py-28 bg-[#111111]">
      <div className="max-w-7xl mx-auto px-8">
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-16">
          {/* Left */}
          <div ref={ref} className={`lg:col-span-2 reveal-left ${visible ? 'visible' : ''}`}>
            <p className="eyebrow mb-5">FAQ</p>
            <h2 className="text-5xl font-bold text-white leading-tight mb-6"
              style={{ fontFamily: 'Playfair Display, serif' }}>
              Common<br />Questions<br />
              <em className="not-italic text-gradient-gold">Answered</em>
            </h2>
            <span className="gold-bar mb-8 block" />
            <p className="text-white/40 text-sm leading-relaxed mb-10">
              Voice-search optimized answers to the questions Austin businesses ask most. Can&apos;t find what you need?
            </p>
            <a href="tel:+15127990115" className="btn-gold flex items-center gap-2 w-fit">
              <i className="ri-phone-fill" />
              Call Us Directly
            </a>

            {/* Schema.org FAQ */}
            <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
              '@context': 'https://schema.org',
              '@type': 'FAQPage',
              mainEntity: faqs.map(f => ({
                '@type': 'Question',
                name: f.q,
                acceptedAnswer: { '@type': 'Answer', text: f.a },
              })),
            }) }} />
          </div>

          {/* Right */}
          <div className={`lg:col-span-3 reveal-right ${visible ? 'visible' : ''}`}>
            <div>
              {faqs.map((faq, i) => (
                <div key={i} style={{ borderBottom: '1px solid #222222' }}>
                  <button
                    onClick={() => setOpen(open === i ? null : i)}
                    className="w-full flex items-start justify-between gap-6 py-6 text-left cursor-pointer group"
                    aria-expanded={open === i}>
                    <span className={`font-semibold text-base leading-snug transition-colors ${
                      open === i ? 'text-[#C4962A]' : 'text-white/70 group-hover:text-white'
                    }`} style={{ fontFamily: 'Playfair Display, serif' }}>
                      {faq.q}
                    </span>
                    <span className={`w-7 h-7 flex items-center justify-center flex-shrink-0 border transition-all mt-0.5 ${
                      open === i ? 'border-[#C4962A] bg-[#C4962A]/10' : 'border-white/15 group-hover:border-[#C4962A]/40'
                    }`}>
                      <i className={`text-xs transition-transform duration-300 ${
                        open === i ? 'ri-subtract-line text-[#C4962A]' : 'ri-add-line text-white/40'
                      }`} />
                    </span>
                  </button>
                  {open === i && (
                    <div className="pb-7 pr-12">
                      <p className="text-white/45 text-sm leading-relaxed">{faq.a}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
